import React, { useState, useEffect } from 'react';
// import { Crisis, Decision } from '../types';
import { formatDate, formatNumber, isMobile, formatRelativeTime } from '../lib/utils';
import { cn } from '../lib/utils';
import DashboardMap from './DashboardMap';

interface CrisisViewProps {
  crisis: Crisis | any; // Using any for backward compatibility with mock data
  onMakeDecision: () => void;
  onBack: () => void;
}

type TabType = 'overview' | 'timeline' | 'decisions' | 'resources' | 'communications' | 'documents';

interface MockDecision {
  id: string;
  title: string;
  decisionMade: string;
  confidence: number;
  createdAt: string;
  createdBy: string;
}

interface TimelineEvent {
  id: string;
  timestamp: string;
  type: 'incident' | 'response' | 'decision' | 'update';
  title: string;
  description: string;
  severity?: number;
  actor?: string;
}

const CrisisView: React.FC<CrisisViewProps> = ({ crisis, onMakeDecision, onBack }) => {
  const [decisions, setDecisions] = useState<MockDecision[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSwipeView, setIsSwipeView] = useState(isMobile());
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showDescription, setShowDescription] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Mock timeline data
  const [timeline] = useState<TimelineEvent[]>([
    {
      id: '1',
      timestamp: crisis.created_at,
      type: 'incident',
      title: 'Crisis Onset',
      description: `Initial ${crisis.type} event detected in ${crisis.location}`,
      severity: crisis.severity,
      actor: 'System'
    },
    {
      id: '2', 
      timestamp: new Date(Date.now() - 86400000 * 3).toISOString(),
      type: 'response',
      title: 'Emergency Response Activated',
      description: `${crisis.response_agencies?.[0] || 'UN OCHA'} activated emergency response protocols`,
      actor: crisis.response_agencies?.[0] || 'UN OCHA'
    },
    {
      id: '3',
      timestamp: new Date(Date.now() - 86400000 * 1).toISOString(), 
      type: 'update',
      title: 'Assessment Update',
      description: `Updated affected population estimates to ${formatNumber(crisis.affected_population)}`,
      actor: 'Field Assessment Team'
    }
  ]);

  // Handle window resize for mobile view and online status
  useEffect(() => {
    const handleResize = () => {
      setIsSwipeView(isMobile());
    };
    
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
      if (navigator.onLine) {
        setLastUpdated(new Date());
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    
    // Auto-refresh every 30 seconds when online
    const interval = setInterval(() => {
      if (navigator.onLine) {
        setLastUpdated(new Date());
      }
    }, 30000);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    fetchDecisions();
  }, [crisis.id]);

  const fetchDecisions = async (): Promise<void> => {
    try {
      const response = await fetch(`/api/crises/${crisis.id}/decisions`);
      if (response.ok) {
        const data: MockDecision[] = await response.json();
        setDecisions(data);
      } else {
        // Fallback to mock data
        setDecisions([]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching decisions:', error);
      setLoading(false);
    }
  };

  const getSeverityColorClass = (severity: number): string => {
    if (severity >= 5) return 'bg-accent-100 text-accent-700 border-accent-300';
    if (severity >= 4) return 'bg-accent-100 text-accent-700 border-accent-300';
    if (severity >= 3) return 'bg-ocean-100 text-ocean-700 border-ocean-300';
    if (severity >= 2) return 'bg-ink-100 text-ink-700 border-ink-300';
    return 'bg-ink-100 text-ink-700 border-ink-300';
  };

  const getSeverityText = (severity: number): string => {
    const levels: Record<number, string> = {
      1: 'Low', 2: 'Minor', 3: 'Medium', 4: 'High', 5: 'Critical'
    };
    return levels[severity] || 'Unknown';
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return 'bg-accent-100 text-accent-700 border-accent-300';
      case 'emerging': return 'bg-accent-100 text-accent-700 border-accent-300';
      case 'stabilizing': return 'bg-ocean-100 text-ocean-700 border-ocean-300';
      case 'resolved': return 'bg-sage-100 text-sage-700 border-sage-300';
      default: return 'bg-ink-100 text-ink-700 border-ink-300';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'decisions', label: 'Decisions' },
    { id: 'resources', label: 'Resources' },
    { id: 'communications', label: 'Communications' },
    { id: 'documents', label: 'Documents' }
  ] as const;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Crisis Location Map */}
            <div className="bg-white border-2 border-ink-300" style={{borderRadius: 0}}>
              <div className="px-6 py-4 border-b-2 border-ink-200">
                <h3 className="font-mono text-label uppercase tracking-ultra text-ink-600 font-bold">Crisis Location</h3>
              </div>
              <div className="p-2">
                <DashboardMap 
                  height="300px" 
                  showControls={false}
                  crisis={crisis}
                  singleCrisis={true}
                />
              </div>
            </div>

            {/* Impact Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-4 border-2 border-ink-300" style={{borderRadius: 0}}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-tiny uppercase tracking-ultra text-ink-500 font-medium mb-1">Total Affected</p>
                    <p className="text-2xl font-display font-bold text-ink-900">
                      {formatNumber(crisis.impact?.affected?.total || crisis.affected_population || 0)}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-ink-100 flex items-center justify-center" style={{borderRadius: 0}}>
                    <div className="w-4 h-4 bg-ink-500" style={{borderRadius: 0}}></div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 border-2 border-ink-300" style={{borderRadius: 0}}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-tiny uppercase tracking-ultra text-ink-500 font-medium mb-1">Casualties</p>
                    <p className="text-2xl font-display font-bold text-accent-600">
                      {formatNumber(crisis.impact?.casualties?.deaths || 0)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatNumber(crisis.impact?.casualties?.injured || 0)} injured
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-warm-100 flex items-center justify-center" style={{borderRadius: 0}}>
                    <div className="w-4 h-4 bg-warm-500" style={{borderRadius: 0}}></div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 border border-gray-200" style={{borderRadius: 0}}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Organizations</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {crisis.respondingOrganizations?.length || crisis.response_agencies?.length || 0}
                    </p>
                    <p className="text-xs text-gray-500">responding</p>
                  </div>
                  <div className="w-8 h-8 bg-cool-100 flex items-center justify-center" style={{borderRadius: 0}}>
                    <div className="w-4 h-4 bg-cool-500" style={{borderRadius: 0}}></div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 border border-gray-200" style={{borderRadius: 0}}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Displaced</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatNumber(crisis.impact?.affected?.displaced || 0)}
                    </p>
                    <p className="text-xs text-gray-500">people</p>
                  </div>
                  <div className="w-8 h-8 bg-amber-100 flex items-center justify-center" style={{borderRadius: 0}}>
                    <div className="w-4 h-4 bg-amber-500" style={{borderRadius: 0}}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Panel */}
            <div className="bg-white border border-gray-200 p-6" style={{borderRadius: 0}}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button 
                  onClick={onMakeDecision}
                  className="bg-warm-500 text-white px-4 py-3 hover:bg-warm-600 transition-colors font-medium" style={{borderRadius: 0}}
                >
                  Make Decision
                </button>
                <button className="bg-cool-700 text-white px-4 py-3 hover:bg-cool-800 transition-colors font-medium" style={{borderRadius: 0}}>
                  Contact Field Teams
                </button>
                <button className="bg-cool-500 text-white px-4 py-3 hover:bg-cool-600 transition-colors font-medium" style={{borderRadius: 0}}>
                  Request Update
                </button>
              </div>
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="bg-white border border-gray-200 p-6" style={{borderRadius: 0}}>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Crisis Timeline</h3>
            <div className="space-y-6">
              {timeline.map((event, index) => (
                <div key={event.id} className="relative">
                  {index !== timeline.length - 1 && (
                    <div className="absolute left-6 top-12 w-0.5 h-16 bg-gray-200"></div>
                  )}
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 flex items-center justify-center text-white font-bold ${
                      event.type === 'incident' ? 'bg-warm-500' :
                      event.type === 'response' ? 'bg-cool-700' :
                      event.type === 'decision' ? 'bg-cool-500' :
                      'bg-neutral-500'
                    }`} style={{borderRadius: '50%'}}>
                      <div className="w-6 h-6 bg-white bg-opacity-30" style={{borderRadius: 0}}></div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-900">{event.title}</h4>
                        <span className="text-sm text-gray-500">
                          {formatRelativeTime(new Date(event.timestamp))}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">{event.description}</p>
                      {event.actor && (
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1" style={{borderRadius: 0}}>
                          {event.actor}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'decisions':
        return (
          <div className="bg-white border border-gray-200 p-6" style={{borderRadius: 0}}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Decision History</h3>
              <button 
                onClick={onMakeDecision}
                className="bg-warm-500 text-white px-4 py-2 hover:bg-warm-600 transition-colors font-medium" style={{borderRadius: 0}}
              >
                + New Decision
              </button>
            </div>
            {decisions.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-neutral-100 flex items-center justify-center mx-auto mb-4" style={{borderRadius: '50%'}}>
                  <div className="w-8 h-8 bg-neutral-400" style={{borderRadius: 0}}></div>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-2">No Decisions Made</h4>
                <p className="text-gray-600 mb-6">No decisions have been recorded for this crisis yet.</p>
                <button 
                  onClick={onMakeDecision}
                  className="bg-warm-500 text-white px-6 py-3 hover:bg-warm-600 transition-colors font-medium" style={{borderRadius: 0}}
                >
                  Make First Decision
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {decisions.map(decision => (
                  <div key={decision.id} className="border border-gray-200 p-4" style={{borderRadius: 0}}>
                    <h4 className="font-semibold text-gray-900 mb-2">{decision.title}</h4>
                    <p className="text-gray-600 mb-3">{decision.decisionMade}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        By {decision.createdBy} on {formatDate(new Date(decision.createdAt))}
                      </span>
                      <span className={`px-2 py-1 text-xs ${
                        decision.confidence >= 80 ? 'bg-green-100 text-green-700' :
                        decision.confidence >= 60 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`} style={{borderRadius: 0}}>
                        {decision.confidence}% confidence
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'resources':
        return (
          <div className="bg-white border border-gray-200 p-6" style={{borderRadius: 0}}>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Response Organizations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(crisis.respondingOrganizations || crisis.response_agencies || []).map((org: string, index: number) => (
                <div key={index} className="border border-gray-200 p-4" style={{borderRadius: 0}}>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-cool-100 flex items-center justify-center" style={{borderRadius: '50%'}}>
                      <span className="text-cool-600 font-bold">{org.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{org}</h4>
                      <p className="text-sm text-gray-500">Active Response</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'communications':
        return (
          <div className="bg-white border border-gray-200 p-6" style={{borderRadius: 0}}>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Communications Hub</h3>
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-cool-100 flex items-center justify-center mx-auto mb-4" style={{borderRadius: '50%'}}>
                <div className="w-8 h-8 bg-cool-400" style={{borderRadius: 0}}></div>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Communications Center</h4>
              <p className="text-gray-600">Real-time communication tools will be available here.</p>
            </div>
          </div>
        );

      case 'documents':
        return (
          <div className="bg-white border border-gray-200 p-6" style={{borderRadius: 0}}>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Crisis Documents</h3>
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-neutral-100 flex items-center justify-center mx-auto mb-4" style={{borderRadius: '50%'}}>
                <div className="w-8 h-8 bg-neutral-400" style={{borderRadius: 0}}></div>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Document Library</h4>
              <p className="text-gray-600">Crisis-related documents and reports will be stored here.</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={cn("crisis-view min-h-screen bg-gray-50", isSwipeView && "pb-20")}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center py-4 text-sm">
            <button 
              onClick={onBack}
              className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
            >
              ← Back to Dashboard
            </button>
          </div>

          {/* Crisis Header */}
          <div className="pb-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-4">
                  <div className={`inline-flex items-center px-3 py-1 text-sm font-medium border ${getSeverityColorClass(crisis.severity)}`} style={{borderRadius: 0}}>
                    <div className={`w-2 h-2 mr-2 ${
                      crisis.severity >= 5 ? 'bg-warm-500' :
                      crisis.severity >= 4 ? 'bg-warm-500' :
                      crisis.severity >= 3 ? 'bg-amber-500' :
                      crisis.severity >= 2 ? 'bg-neutral-500' :
                      'bg-neutral-500'
                    }`} style={{borderRadius: '50%'}}></div>
                    {getSeverityText(crisis.severity)} Severity
                  </div>
                  
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium border ${getStatusColor(crisis.status)}`} style={{borderRadius: 0}}>
                    <div className={`w-2 h-2 mr-2 ${
                      crisis.status === 'active' ? 'bg-warm-500' :
                      crisis.status === 'emerging' ? 'bg-warm-500' :
                      crisis.status === 'stabilizing' ? 'bg-amber-500' :
                      'bg-cool-500'
                    }`} style={{borderRadius: '50%'}}></div>
                    {crisis.status?.charAt(0).toUpperCase() + crisis.status?.slice(1) || 'Unknown'}
                  </span>

                  <div className={`inline-flex items-center px-3 py-1 text-xs font-medium ${
                    isOnline ? 'bg-cool-100 text-cool-700' : 'bg-warm-100 text-warm-700'
                  }`} style={{borderRadius: 0}}>
                    <div className={`w-2 h-2 mr-2 ${isOnline ? 'bg-cool-500' : 'bg-warm-500'}`} style={{borderRadius: '50%'}}></div>
                    {isOnline ? 'Online' : 'Offline'}
                  </div>
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-4">{crisis.title}</h1>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <span className="ml-2 font-medium text-gray-900 capitalize">{crisis.type}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Location:</span>
                    <span className="ml-2 font-medium text-gray-900">{crisis.location}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Affected:</span>
                    <span className="ml-2 font-medium text-gray-900">{formatNumber(crisis.affected_population)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Started:</span>
                    <span className="ml-2 font-medium text-gray-900">{formatDate(new Date(crisis.created_at))}</span>
                  </div>
                </div>

                {crisis.description && (
                  <div className="mt-4">
                    <p className="text-gray-600 leading-relaxed">
                      {crisis.description}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className={isSwipeView ? "hidden" : "border-b border-gray-200"}>
            <nav className="flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as TabType)}
                  className={cn(
                    "py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap",
                    activeTab === tab.id
                      ? "border-warm-500 text-warm-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isSwipeView && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-10">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={cn(
                  "flex flex-col items-center py-2 px-4 min-w-[80px] text-xs",
                  activeTab === tab.id ? "text-warm-600 bg-warm-50" : "text-gray-500"
                )}
              >
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default CrisisView;